import React, { useState, useRef, useEffect, useCallback } from 'react';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { Button, Nav, Navbar } from 'react-bootstrap';
import Select from 'react-select';
import LoadingSpinner from '../Loading-Component/Loading';
import { useNavigate } from 'react-router';
import { consumePreloadedData } from '../../utils/preloadStore';
import { useDeviceInfo } from '../DeviceInfo';
import { useAuth } from '../../contexts/AuthContext';
import ManageSecurityRefactored from './ManageSecurity/index';
import QueryTable from '../QueryReport-Component/QueryTable';
import ReportSummaryView from '../SummaryReport-Component/ReportSummaryView';
import { formatShortDate } from '../../utils/helpers';
import 'react-virtualized/styles.css';
import './managerReport.scss';
import opusLogos from '../../images/manager-report/footer-logos-v2.png';
import gigpig from '../../images/manager-report/gigpig.svg';
import copiaLogo from '../../images/manager-report/Copia247.png';
import { FaDownload, FaPoundSign, FaStar, FaLock, FaCheck, FaHourglassHalf } from "react-icons/fa";
import {
    BsCircleFill,
    BsExclamationTriangleFill,
    BsClockFill,
    BsCreditCardFill,
    BsCalendarWeekFill,
    BsLightningChargeFill,
    BsFunnelFill,
    BsChevronUp,
    BsCheckCircleFill,
    BsFileEarmarkTextFill,
} from 'react-icons/bs';
import { MdEdit, MdVisibility } from "react-icons/md";
import ChatBot from "../ChatBot-Component/ChatBot";
import CostingSummary from '../CostingSummary-Component/CostingSummary';
import CostingSummaryFilters from '../CostingSummary-Component/components/CostingSummaryFilters';
import SecurityFilters from './ManageSecurity/components/SecurityFilters';
import FulfillmentSummary from './ManageSecurity/components/FulfillmentSummary';
import FilterSidebar from './FilterSidebar';
import TaggingPatrol from './TaggingPatrol/index';
import Occurrences from './Occurrences/index';
import Documents from './Documents/index';
import { IoHomeOutline, IoDownloadOutline, IoLogOutOutline } from 'react-icons/io5';
import { HiOutlineUserGroup } from 'react-icons/hi';

//components
import ReportTableRow from './components/ReportTableRow';
import ReportTableCard from './components/ReportTableCard';
import QueryFormModal from './components/QueryFormModal';
import RmSiteListModal from './components/RmSiteListModal';
import SignoffModal from './components/SignoffModal';
import ReportNotice from './components/ReportNotice';
import Filterstop from './components/Filterstop';

//hooks
import useReportData from './hooks/useReportData';
import useManagerReportTour from './hooks/useManagerReportTour';
import useFulfillmentData from './hooks/useFulfillmentData';
import useFeatureAccess from './hooks/useFeatureAccess';
import useRating from './hooks/useRating';
import useMobileNav from './hooks/useMobileNav';
import useSecurityFilters from './hooks/useSecurityFilters';
import useQueryFilters from './hooks/useQueryFilters';
import useCostingSummaryFilters from './hooks/useCostingSummaryFilters';
import useQueryData from './hooks/useQueryData';
import useCostingSummaryData from './hooks/useCostingSummaryData';
import useGigPig from './hooks/useGigPig';

//utils
import { handleExcelExport } from './utils/exportHelpers';
import {
    DEFAULT_FILTERS,
    COSTING_COLUMNS,
    QUERY_COLUMNS,
    ENTERTAINMENT_STORAGE_KEY,
    sidebarSelectStyles,
} from './utils/reportHelpers';

const TABLE_LEGENDS = {
    costing: [
        { icon: <BsCircleFill color="blue" />, label: 'New' },
        { icon: <BsCreditCardFill color="green" />, label: 'Credited' },
        { icon: <BsClockFill color="orange" />, label: 'Late' },
        { icon: <BsExclamationTriangleFill color="red" />, label: 'Missing' },
        { icon: <BsCalendarWeekFill color="#64748b" />, label: 'Future' },
    ],
    costingSummary: [
        { icon: <BsCheckCircleFill color="#28a745" />, label: 'Billed' },
        { icon: <BsClockFill color="#ffc107" />, label: 'Not Yet Billed' },
        { icon: <BsCreditCardFill color="#17a2b8" />, label: 'Credit' },
    ],
    queries: [
        { icon: <FaPoundSign color="green" />, label: 'Billing' },
        { icon: <FaStar color="orange" />, label: 'Quality' },
        { icon: <MdEdit color="blue" />, label: 'Editable' },
        { icon: <MdVisibility color="gray" />, label: 'Read Only' },
    ],
    security: [
        { icon: <FaLock className="lock-icon" />, label: 'Padlocked rows cannot be removed, only duplicated' },
    ],
    tagging: [
        { icon: <BsCircleFill color="#4CAF50" />, label: 'Completed' },
        { icon: <BsCircleFill color="#FFB300" />, label: 'Completed Late' },
        { icon: <BsCircleFill color="#f44336" />, label: 'Not Completed' },
        { icon: <BsCircleFill color="#90CAF9" />, label: 'Not Yet Due' },
    ],
    occurrences: [
        { icon: <BsCircleFill color="#CB2C84" />, label: 'Incident Report' },
        { icon: <BsCircleFill color="#f44336" />, label: 'Accident Report' },
        { icon: <BsCircleFill color="#FF9800" />, label: 'Near Miss' },
        { icon: <BsCircleFill color="#4CAF50" />, label: 'Health & Safety' },
        { icon: <BsCircleFill color="#FF5722" />, label: 'Fire Alarm' },
        { icon: <BsCircleFill color="#2196F3" />, label: 'First Aid' },
    ],
    fulfillment: [
        { icon: <BsCircleFill color="#118dff" />, label: 'Confirmed' },
        { icon: <BsCircleFill color="#FFD700" />, label: 'Allocated' },
        { icon: <BsCircleFill color="#4CAF50" />, label: 'Notified' },
        { icon: <BsCircleFill color="#f44336" />, label: 'Not Allocated' },
    ],
    documents: [
        { icon: <BsFileEarmarkTextFill color="#4fc3f7" />, label: 'Document Available' },
    ],
};

const ReportFooter = React.memo(function ReportFooter({ activeComponent, costingTabView, focusMode, logo }) {
    const legendKey = activeComponent === 'costing' && costingTabView === 'summary'
        ? 'costingSummary'
        : activeComponent;

    const legendItems = TABLE_LEGENDS[legendKey] || [];
    const isHidden = activeComponent === 'costing' && costingTabView === 'costing' && !focusMode;

    return (
        <div className={`footer d-lg-flex justify-content-between align-items-center ps-3 my-3 ${isHidden ? 'd-none' : ''}`}>
            {legendItems.length > 0 && (
                <div className="rounded bg-light">
                    <div className="tableKey d-flex align-items-center flex-wrap">
                        {legendItems.map(({ icon, label }) => (
                            <div key={label} className="d-flex align-items-center">
                                {icon}
                                <p>{label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'end' }}>
                <img style={{ height: '2rem' }} className="mt-3 mt-lg-0" src={logo} alt="Opus Apeiro" />
            </div>
        </div>
    );
});

export function ManagerReport() {
    const { isAuthenticated, userId, featureFlags, logout } = useAuth();
    const navigate = useNavigate();

    // consumePreloadedData reads and clears the store in one call
    const preRef = useRef(consumePreloadedData());
    const pre = preRef.current;

    const prevFilteredDataRef = useRef();
    const visibleDataRef = useRef([]);
    const { deviceInfo } = useDeviceInfo();

    const [activeComponent, setActiveComponent] = useState('costing');
    const [activeHeaderTab, setActiveHeaderTab] = useState('report');
    const [filterValues, setFilterValues] = useState(DEFAULT_FILTERS);
    const summary = 0;

    const {
        reportData,
        DataAfterFilter,
        loading,
        setLoading,
        responseMessage,
        settingsValues,
        fetchReportTable,
        handleDataChange,
    } = useReportData({ deviceInfo, isAuthenticated, summary, activeComponent, filterValues, preloadedData: pre });

    const [costingTabView, setCostingTabView] = useState('costing');
    const [showFilterSidebar, setShowFilterSidebar] = useState(false);
    const [showAI, setShowAI] = useState(false);
    const [queryIdToEdit, setQueryIdToEdit] = useState(null);

    const urlParams = React.useMemo(() => new URLSearchParams(window.location.search.toLowerCase()), []);
    const [focusMode, setFocusMode] = useState(true);

    const [securityData, setSecurityData] = useState(() => {
        if (pre?.sites) {
            return {
                sites: pre.sites || [],
                roles: pre.roles?.item1 || [],
                financialWeeks: pre.financialWeeks?.item1 || [],
                securitySummary: Array.isArray(pre.fulfillment) ? pre.fulfillment : [],
            };
        }
        return { sites: [], roles: [], financialWeeks: [], securitySummary: [] };
    });
    const [securityDataLoaded, setSecurityDataLoaded] = useState(() => !!pre?.sites);
    const [queryTabView, setQueryTabView] = useState('open');
    const [taggingData, setTaggingData] = useState(() => pre?.tagging ? { taggingData: pre.tagging } : null);
    const [taggingDataLoaded, setTaggingDataLoaded] = useState(() => !!pre?.tagging);
    const taggingRef = useRef(null);

    const [occurrenceData, setOccurrenceData] = useState(() => pre?.occurrences ? { occurrenceData: pre.occurrences } : null);
    const [occurrenceDataLoaded, setOccurrenceDataLoaded] = useState(() => !!pre?.occurrences);
    const occurrenceRef = useRef(null);
    const [occurrenceTab, setOccurrenceTab] = useState('overview');
    const [occurrenceSummaryEnabled, setOccurrenceSummaryEnabled] = useState(false);

    const [documentData, setDocumentData] = useState(() => pre?.documents ? { documentData: pre.documents } : null);
    const [documentDataLoaded, setDocumentDataLoaded] = useState(() => !!pre?.documents);
    const documentRef = useRef(null);

    const { logFeatureAccess } = useFeatureAccess({ deviceInfo });
    const { handleRatingSubmit } = useRating({ userId, setLoading });
    const { bottomNavExpanded, setBottomNavExpanded, mobileFilters } = useMobileNav();

    const {
        securitySelectedSite,
        setSecuritySelectedSite,
        securityDateFilters,
        setSecurityDateFilters,
        securityFinancialWeek,
        setSecurityFinancialWeek,
        securityCreateShiftVals,
        setSecurityCreateShiftVals,
        handleSecuritySiteSelect,
        handleSecurityDateChange,
        handleSecurityFinancialWeekSelect,
    } = useSecurityFilters();

    const {
        queryData,
        queryResolutionTypes,
        setQueryResolutionTypes,
    } = useQueryData({ preloadedData: pre, urlParams, setLoading });

    const {
        queryFilters,
        setQueryFilters,
        queryDeptOptions,
        querySupplierOptions,
        queryResolutionTypeOptions,
    } = useQueryFilters({ queryData, queryResolutionTypes });

    const {
        costingSummaryData,
        setCostingSummaryData,
        costingSummaryDataLoaded,
        setCostingSummaryDataLoaded,
    } = useCostingSummaryData({ preloadedData: pre });

    const {
        costingSummaryFilters,
        setCostingSummaryFilters,
        costingSummaryClearRef,
        handleCostingSummaryClearFilters,
    } = useCostingSummaryFilters();

    const {
        uniqueData,
        showModal,
        setShowModal,
        gigPigEnabled,
        handleGigpigPortal,
        handleRmSiteSelect,
    } = useGigPig({ reportData, settingsValues, userId, deviceInfo, logFeatureAccess });

    // Signoff modal state (lifted to prevent inner-component remount issues)
    const [showSignOffModal, setShowSignOffModal] = useState(false);
    const [signOffRowData, setSignOffRowData] = useState({});
    const updateLineItemRef = useRef(null);

    const handleManagerSignoffModal = useCallback((data) => {
        setShowSignOffModal(true);
        setSignOffRowData(data);
    }, []);

    const signoffModalHide = useCallback(() => {
        const paramApprove = new URLSearchParams(window.location.search);
        if (paramApprove.has('approve')) {
            paramApprove.delete('approve');
            const newUrl = `${window.location.pathname}`;
            window.history.replaceState(null, '', newUrl);
        }
        setShowSignOffModal(false);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const { fulfillmentLoading } = useFulfillmentData({
        activeComponent,
        securityData,
        setSecurityData,
        securityDataLoaded,
        setSecurityDataLoaded,
    });

    useEffect(() => {
        const queryId = urlParams.get('queryid');
        if (queryId && isAuthenticated) {
            setActiveComponent('queries');
            setLoading(true);
            setQueryIdToEdit(parseInt(queryId));
        }
    }, [isAuthenticated]);

    const toggleFocusMode = () => {
        setFocusMode(!focusMode);
    };

    const [costingVisibleCols, setCostingVisibleCols] = useState(
        () => COSTING_COLUMNS.map(c => c.key)
    );

    const [queryVisibleCols, setQueryVisibleCols] = useState(
        () => QUERY_COLUMNS.map(c => c.key)
    );

    const [showQueryFormPopup, setShowQueryFormPopup] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const openQueryFormModal = (rowData) => {
        setSelectedRowData(rowData);
        setShowQueryFormPopup(true);
    };
    const closeQueryFormModal = () => {
        setSelectedRowData(null);
        setShowQueryFormPopup(false);
        setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    };
    const handleQueryFormSubmitSuccess = async () => {
        try {
            setLoading(true);
            await fetchReportTable();
            setSelectedRowData(null);
            setShowQueryFormPopup(false);
        } catch (error) {
            console.error('Error refreshing data:', error);
            setLoading(false);
        }
    };

    const handleLogout = useCallback(() => {
        logout();
        window.location.replace('/login');
    }, [logout]);

    const onExcelExport = () => handleExcelExport(visibleDataRef.current, formatShortDate);

    useEffect(() => {
        document.title = "C247 + Costings Report";
        if (isAuthenticated && activeComponent === 'costing' && !reportData.length) {
            fetchReportTable();
        }
    }, [deviceInfo, summary, isAuthenticated, activeComponent]);

    const [showSecurityCosting, setShowSecurityCostings] = useState(false);
    const shouldShowButton = !!featureFlags.copiaDirectRostering;
    const shouldShowQueries = !!featureFlags.copiaQueryApprove;
    const shouldShowOccurrences = !!featureFlags.copiaOccurrenceBasic;
    const shouldShowRequirements = !!featureFlags.copiaDirectRostering;
    const shouldShowTagging = !!featureFlags.copiaTagging;
    const shouldShowBase = !!featureFlags.copiaBase;

    const hasTaggingData = !taggingDataLoaded || (taggingData?.taggingData?.length > 0);
    const hasOccurrenceData = !occurrenceDataLoaded || (occurrenceData?.occurrenceData?.length > 0);
    const hasDocumentData = !documentDataLoaded || (documentData?.documentData?.length > 0);

    useEffect(() => {
        if (reportData.length > 0) {
            const ShowSecurityCosting = Object.assign(...new Set(reportData.map(item => item.showSecurityCostings)));
            setShowSecurityCostings(ShowSecurityCosting);
            if (!featureFlags.copiaAi) {
                setShowAI(false);
            } else {
                setShowAI(true);
            }
        }
    }, [reportData, featureFlags]);

    useManagerReportTour({ reportData, activeComponent });

    const handleSummaryTabClick = useCallback(() => {
        setCostingTabView('summary');
        logFeatureAccess('CostingSummary');
    }, [logFeatureAccess]);

    if (!isAuthenticated) {
        return <LoadingSpinner loading={true} />;
    }

    return (
        <div className="manager-report-container">
            <LoadingSpinner loading={loading} />
            <RmSiteListModal
                show={showModal}
                onHide={() => setShowModal(false)}
                uniqueData={uniqueData}
                onSiteSelect={handleRmSiteSelect}
            />
            <QueryFormModal
                show={showQueryFormPopup}
                onHide={closeQueryFormModal}
                selectedRowData={selectedRowData}
                userId={userId}
                onSubmitSuccess={handleQueryFormSubmitSuccess}
            />
            <SignoffModal
                showModal={showSignOffModal}
                onHide={signoffModalHide}
                rowdata={signOffRowData}
                emailAddress={userId}
                setLoading={setLoading}
                deviceinfo={deviceInfo}
                settingsValues={settingsValues}
                Updatelineitem={(lineid, ratings, signatures) => updateLineItemRef.current?.(lineid, ratings, signatures)}
                openQueryForm={openQueryFormModal}
            />

            {(isMobileOnly || isTablet) && (
                <FilterSidebar show={showFilterSidebar} onHide={() => setShowFilterSidebar(false)}>
                    {activeComponent === 'costing' && costingTabView === 'costing' && (<>
                        <Filterstop
                            onDatachange={handleDataChange}
                            DataBefore={reportData}
                            DataAfter={DataAfterFilter}
                            setLoading={setLoading}
                            onSearchChange={() => {}}
                            filterValues={filterValues}
                            setFilterValues={setFilterValues}
                        />
                        <div className="sidebar-filter-section">
                            <div className="sidebar-filter-item">
                                <label>Columns</label>
                                <div className="sidebar-col-toggles">
                                    {COSTING_COLUMNS.filter(c => c.key !== 'gigpig' || featureFlags.copiaQueryApprove).map(col => (
                                        <label key={col.key} className="sidebar-col-toggle">
                                            <input
                                                type="checkbox"
                                                checked={costingVisibleCols.includes(col.key)}
                                                onChange={() => setCostingVisibleCols(prev =>
                                                    prev.includes(col.key) ? prev.filter(k => k !== col.key) : [...prev, col.key]
                                                )}
                                            />
                                            <span>{col.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>)}
                    {activeComponent === 'costing' && costingTabView === 'summary' && (
                        <CostingSummaryFilters
                            rawData={costingSummaryData}
                            filters={costingSummaryFilters}
                            setFilters={setCostingSummaryFilters}
                            summarySelection={null}
                            detailSelection={null}
                            onClearAll={handleCostingSummaryClearFilters}
                        />
                    )}
                    {activeComponent === 'queries' && (
                        <div className="sidebar-filter-section">
                            <div className="sidebar-filter-item">
                                <label>Search</label>
                                <input
                                    type="text"
                                    placeholder="Free Search"
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }}
                                    value={queryFilters.searchTerm}
                                    onChange={(e) => setQueryFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                                />
                            </div>
                            <div className="sidebar-filter-item">
                                <label>Department</label>
                                <Select
                                    value={queryFilters.roleDepartment ? queryDeptOptions.find(o => o.value === queryFilters.roleDepartment) : null}
                                    onChange={(selected) => setQueryFilters(prev => ({ ...prev, roleDepartment: selected ? selected.value : '' }))}
                                    options={queryDeptOptions}
                                    placeholder="Select"
                                    isClearable
                                    isSearchable
                                    classNamePrefix="react-select"
                                    styles={sidebarSelectStyles}
                                />
                            </div>
                            <div className="sidebar-filter-item">
                                <label>Resolution Types</label>
                                <Select
                                    value={queryFilters.resolutionType ? queryResolutionTypeOptions.find(o => o.value === queryFilters.resolutionType) : null}
                                    onChange={(selected) => setQueryFilters(prev => ({ ...prev, resolutionType: selected ? selected.value : '' }))}
                                    options={queryResolutionTypeOptions}
                                    placeholder="Select"
                                    isClearable
                                    isSearchable
                                    classNamePrefix="react-select"
                                    styles={sidebarSelectStyles}
                                />
                            </div>
                            <div className="sidebar-filter-item">
                                <label>Suppliers</label>
                                <Select
                                    value={queryFilters.supplier ? querySupplierOptions.find(o => o.value === queryFilters.supplier) : null}
                                    onChange={(selected) => setQueryFilters(prev => ({ ...prev, supplier: selected ? selected.value : '' }))}
                                    options={querySupplierOptions}
                                    placeholder="Select"
                                    isClearable
                                    isSearchable
                                    classNamePrefix="react-select"
                                    styles={sidebarSelectStyles}
                                />
                            </div>
                            <button
                                className="btn btn-sm w-100 mt-2"
                                style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }}
                                onClick={() => setQueryFilters({ searchTerm: '', roleDepartment: '', resolutionType: '', supplier: '' })}
                            >
                                Clear All
                            </button>
                            <div className="sidebar-filter-item mt-3">
                                <label>Columns</label>
                                <div className="sidebar-col-toggles">
                                    {QUERY_COLUMNS.map(col => (
                                        <label key={col.key} className="sidebar-col-toggle">
                                            <input
                                                type="checkbox"
                                                checked={queryVisibleCols.includes(col.key)}
                                                onChange={() => setQueryVisibleCols(prev =>
                                                    prev.includes(col.key) ? prev.filter(k => k !== col.key) : [...prev, col.key]
                                                )}
                                            />
                                            <span>{col.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {activeComponent === 'security' && (
                        <div className="sidebar-filter-section">
                            <SecurityFilters
                                selectedSite={securitySelectedSite}
                                sites={securityData.sites || []}
                                onSiteSelect={handleSecuritySiteSelect}
                                dateFrom={securityDateFilters.shiftStart}
                                dateTo={securityDateFilters.shiftFinish}
                                onDateChange={handleSecurityDateChange}
                                selectedFinancialWeek={securityFinancialWeek}
                                financialWeeks={securityData.financialWeeks || []}
                                onFinancialWeekSelect={handleSecurityFinancialWeekSelect}
                            />
                        </div>
                    )}
                    {activeComponent === 'tagging' && taggingRef.current?.renderFilters?.()}
                    {activeComponent === 'occurrences' && occurrenceRef.current?.renderFilters?.()}
                    {activeComponent === 'documents' && documentRef.current?.renderFilters?.()}
                </FilterSidebar>
            )}
            {(isMobileOnly || isTablet) && (<>
                <div className="mobile-header">
                    <div className="mobile-header__bar">
                        <img src={copiaLogo} alt="Copia247" className="mobile-header__logo" />
                        <div className="mobile-header__tabs">
                            {shouldShowBase && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'costing' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => setActiveComponent('costing')}
                                >
                                    Costings
                                </button>
                            )}
                            {shouldShowQueries && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'queries' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => { setActiveComponent('queries'); setLoading(true); }}
                                >
                                    Queries
                                </button>
                            )}
                            {shouldShowRequirements && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'security' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => { setActiveComponent('security'); logFeatureAccess('ManageSecurity'); }}
                                >
                                    Security
                                </button>
                            )}
                            {shouldShowTagging && hasTaggingData && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'tagging' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => { setActiveComponent('tagging'); logFeatureAccess('Tagging'); }}
                                >
                                    Tagging
                                </button>
                            )}
                            {shouldShowOccurrences && hasOccurrenceData && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'occurrences' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => { setActiveComponent('occurrences'); logFeatureAccess('Occurrences'); }}
                                >
                                    Occurrences
                                </button>
                            )}
                            {shouldShowBase && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'fulfillment' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => { setActiveComponent('fulfillment'); logFeatureAccess('Fulfillment'); }}
                                >
                                    Fulfillment
                                </button>
                            )}
                            {shouldShowBase && hasDocumentData && (
                                <button
                                    className={`mobile-header__tab ${activeComponent === 'documents' ? 'mobile-header__tab--active' : ''}`}
                                    onClick={() => { setActiveComponent('documents'); logFeatureAccess('Documents'); }}
                                >
                                    Documents
                                </button>
                            )}
                        </div>
                        <button className="mobile-header__filter-btn" onClick={() => setShowFilterSidebar(true)}>
                            <BsFunnelFill size={18} />
                        </button>
                    </div>
                </div>
                <div className="mobile-title">Manager Report</div>

                {activeComponent === 'costing' && (
                    <div className="mobile-sub-tabs">
                        <button
                            className={`mobile-sub-tab ${costingTabView === 'costing' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'}`}
                            onClick={() => setCostingTabView('costing')}
                        >
                            Details
                        </button>
                        <button
                            className={`mobile-sub-tab ${costingTabView === 'summary' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'}`}
                            onClick={handleSummaryTabClick}
                        >
                            Summary
                        </button>
                    </div>
                )}

                {activeComponent === 'queries' && (
                    <div className="mobile-sub-tabs">
                        <button
                            className={`mobile-sub-tab ${queryTabView === 'open' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'}`}
                            onClick={() => setQueryTabView('open')}
                        >
                            Open
                        </button>
                        <button
                            className={`mobile-sub-tab ${queryTabView === 'closed' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'}`}
                            onClick={() => setQueryTabView('closed')}
                        >
                            Closed
                        </button>
                    </div>
                )}

                {activeComponent === 'occurrences' && (
                    <div className="mobile-sub-tabs">
                        <button
                            className={`mobile-sub-tab ${occurrenceTab === 'overview' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'}`}
                            onClick={() => setOccurrenceTab('overview')}
                        >
                            Overview
                        </button>
                        <button
                            className={`mobile-sub-tab ${occurrenceTab === 'detail' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'}`}
                            onClick={() => setOccurrenceTab('detail')}
                        >
                            Detail
                        </button>
                        <button
                            className={`mobile-sub-tab ${occurrenceTab === 'summary' ? 'mobile-sub-tab--active' : 'mobile-sub-tab--inactive'} ${!occurrenceSummaryEnabled ? 'table-tab--disabled' : ''}`}
                            onClick={() => occurrenceSummaryEnabled && setOccurrenceTab('summary')}
                            disabled={!occurrenceSummaryEnabled}
                        >
                            {featureFlags.copiaOccurrenceAdvanced ? 'Follow-Up' : 'View'}
                        </button>
                    </div>
                )}

            </>
            )}

            {!isMobileOnly && !isTablet && (
            <div className={`${focusMode ? 'd-block' : 'd-none'} report-header w-100 pt-3 pb-2`}>
                <div className="header-v3">
                    <div className="header-v3__inner">
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: '30px' }}>
                            <img src={copiaLogo} alt="Copia247" className="header-logo" />
                            <button
                                className={`header-tab ${activeHeaderTab === 'report' ? 'header-tab--active' : ''}`}
                                onClick={() => setActiveHeaderTab('report')}
                            >
                                Manager Report
                            </button>
                            <Button
                                className="download-export-btn"
                                onClick={onExcelExport}
                            >
                                <FaDownload /> Export
                            </Button>
                            {gigPigEnabled && gigPigEnabled[0] === true && (
                                <Button
                                    className={`book-entertainment-btn${!localStorage.getItem(ENTERTAINMENT_STORAGE_KEY) ? ' entertainment-pulse' : ''}`}
                                    onAnimationEnd={(e) => e.currentTarget.classList.remove('entertainment-pulse')}
                                    onClick={() => {
                                        localStorage.setItem(ENTERTAINMENT_STORAGE_KEY, 'true');
                                        handleGigpigPortal();
                                    }}
                                >
                                    <img src={gigpig} alt="" className="gigpig-logo" />
                                    Book Entertainment
                                </Button>
                            )}
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            {showAI && (
                                <button className="chatbot-header-btn" type="button">
                                    <BsLightningChargeFill />
                                </button>
                            )}
                            <button className="logout-header-btn" type="button" onClick={handleLogout} title="Logout">
                                <IoLogOutOutline size={18} /> Logout
                            </button>
                        </div>
                    </div>
                </div>

                {activeHeaderTab === 'report' && (
                    <div className="tabs-row mt-2">
                        <Navbar expand="lg">
                            <Navbar.Toggle aria-controls="basic-navbar-nav" />
                            <Navbar.Collapse id="basic-navbar-nav">
                                <Nav className="me-auto">
                                    {shouldShowBase && (
                                        <Nav.Link
                                            className={activeComponent === 'costing' ? 'active-link costing-nav' : 'costing-nav'}
                                            onClick={() => setActiveComponent('costing')}
                                        >
                                            Costings
                                        </Nav.Link>
                                    )}
                                    {shouldShowQueries && (
                                        <Nav.Link
                                            className={activeComponent === 'queries' ? 'active-link' : ''}
                                            onClick={() => {
                                                setActiveComponent('queries');
                                                setLoading(true);
                                            }}
                                        >
                                            Queries
                                        </Nav.Link>
                                    )}
                                    {shouldShowRequirements && (
                                        <Nav.Link
                                            className={`manage-security-nav ${activeComponent === 'security' ? 'active-link button-nav' : 'button-nav'}`}
                                            onClick={() => { setActiveComponent('security'); logFeatureAccess('ManageSecurity'); }}
                                        >
                                            Requirements
                                        </Nav.Link>
                                    )}
                                    {shouldShowTagging && hasTaggingData && (
                                        <Nav.Link
                                            className={activeComponent === 'tagging' ? 'active-link' : ''}
                                            onClick={() => { setActiveComponent('tagging'); logFeatureAccess('Tagging'); }}
                                        >
                                            Tagging/Patrol
                                        </Nav.Link>
                                    )}
                                    {shouldShowOccurrences && hasOccurrenceData && (
                                        <Nav.Link
                                            className={activeComponent === 'occurrences' ? 'active-link' : ''}
                                            onClick={() => { setActiveComponent('occurrences'); logFeatureAccess('Occurrences'); }}
                                        >
                                            Occurrences
                                        </Nav.Link>
                                    )}
                                    {shouldShowBase && (
                                        <Nav.Link
                                            className={activeComponent === 'fulfillment' ? 'active-link' : ''}
                                            onClick={() => { setActiveComponent('fulfillment'); logFeatureAccess('Fulfillment'); }}
                                        >
                                            Fulfillment
                                        </Nav.Link>
                                    )}
                                    {shouldShowBase && hasDocumentData && (
                                        <Nav.Link
                                            className={activeComponent === 'documents' ? 'active-link' : ''}
                                            onClick={() => { setActiveComponent('documents'); logFeatureAccess('Documents'); }}
                                        >
                                            Documents
                                        </Nav.Link>
                                    )}
                                </Nav>
                            </Navbar.Collapse>
                        </Navbar>
                    </div>
                )}
            </div>
            )}

            {activeHeaderTab === 'entertainment' && !isMobileOnly && !isTablet && (
                <div className="entertainment-placeholder d-flex align-items-center justify-content-center" style={{ minHeight: '60vh', color: 'white' }}>
                    <div className="text-center">
                        <h2 style={{ fontSize: '24px', fontWeight: 500, marginBottom: '12px' }}>Manage Entertainment</h2>
                        <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)' }}>Coming soon</p>
                    </div>
                </div>
            )}

            <div className={`manager-reports-container d-flex flex-column ${(isMobileOnly || isTablet) ? 'mobile-content-padded' : ''}`} style={activeHeaderTab !== 'report' && !isMobileOnly && !isTablet ? { display: 'none' } : {}}>
                {(isMobileOnly || isTablet) ? (
                    <div className="content-panel d-flex flex-grow-1 flex-column align-items-center justify-content-center">
                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Coming soon</p>
                    </div>
                ) : activeComponent === 'costing' ? (

                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        {!isMobileOnly && !isTablet && (
                            <div className="table-level-tabs mb-2 d-flex align-items-center">
                                <button
                                    className={`table-tab ${costingTabView === 'costing' ? 'active' : ''}`}
                                    onClick={() => setCostingTabView('costing')}
                                >
                                    Detail
                                </button>

                                <button
                                    className={`table-tab ${costingTabView === 'summary' ? 'active' : ''}`}
                                    onClick={handleSummaryTabClick}
                                >
                                    Summary
                                </button>
                            </div>
                        )}

                        {costingTabView === 'costing' ? (
                            <div className="d-flex flex-grow-1">
                                <div className="report-table-container">
                                    {responseMessage ? (
                                        <ReportNotice message={responseMessage} />
                                    ) : (isMobileOnly || isTablet) ? (
                                        <ReportTableCard
                                            data={DataAfterFilter}
                                            featureFlags={featureFlags}
                                            onRatingSubmit={handleRatingSubmit}
                                            onSignoffModal={handleManagerSignoffModal}
                                            onQueryFormOpen={openQueryFormModal}
                                            prevFilteredDataRef={prevFilteredDataRef}
                                            updateLineItemRef={updateLineItemRef}
                                        />
                                    ) : (
                                        <ReportTableRow
                                            data={DataAfterFilter}
                                            reportData={reportData}
                                            featureFlags={featureFlags}
                                            filterValues={filterValues}
                                            setFilterValues={setFilterValues}
                                            costingVisibleCols={costingVisibleCols}
                                            setCostingVisibleCols={setCostingVisibleCols}
                                            columnConfig={COSTING_COLUMNS.filter(c => c.key !== 'gigpig' || featureFlags.copiaQueryApprove)}
                                            onRatingSubmit={handleRatingSubmit}
                                            onSignoffModal={handleManagerSignoffModal}
                                            onQueryFormOpen={openQueryFormModal}
                                            onDataChange={handleDataChange}
                                            visibleDataRef={visibleDataRef}
                                            prevFilteredDataRef={prevFilteredDataRef}
                                            updateLineItemRef={updateLineItemRef}
                                            setLoading={setLoading}
                                            DataAfterFilter={DataAfterFilter}
                                            mobileFilters={mobileFilters}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-grow-1">
                                <div className="report-table-container">
                                    <CostingSummary
                                        preloadedData={costingSummaryData}
                                        setPreloadedData={setCostingSummaryData}
                                        dataLoaded={costingSummaryDataLoaded}
                                        setDataLoaded={setCostingSummaryDataLoaded}
                                        filters={costingSummaryFilters}
                                        setFilters={setCostingSummaryFilters}
                                        onRegisterClearSelections={(fn) => { costingSummaryClearRef.current = fn; }}
                                        isMobile={isMobileOnly || isTablet}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ) : activeComponent === 'queries' ? (
                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        {!isMobileOnly && !isTablet && (
                            <div className="table-level-tabs mb-2 d-flex align-items-center">
                                <button
                                    className={`table-tab ${queryTabView === 'open' ? 'active' : ''}`}
                                    onClick={() => setQueryTabView('open')}
                                >
                                    Open
                                </button>
                                <button
                                    className={`table-tab ${queryTabView === 'closed' ? 'active' : ''}`}
                                    onClick={() => setQueryTabView('closed')}
                                >
                                    Closed
                                </button>
                            </div>
                        )}
                        <div className="report-table-container query-table-container">
                            <QueryTable
                                queryIdToEdit={queryIdToEdit}
                                setQueryIdToEdit={setQueryIdToEdit}
                                preloadedData={queryData}
                                loading={loading}
                                setLoading={setLoading}
                                filters={queryFilters}
                                setFilters={setQueryFilters}
                                onResolutionTypesLoaded={setQueryResolutionTypes}
                                toggleStatus={queryTabView}
                                setToggleStatus={setQueryTabView}
                                visibleColumns={queryVisibleCols}
                                columnConfig={QUERY_COLUMNS}
                                onColumnsChange={setQueryVisibleCols}
                            />
                        </div>
                    </div>

                    ) : activeComponent === 'security' ? (
                    <>
                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        <div className="d-flex flex-grow-1">
                            <div className="report-table-container security-container">
                                <ManageSecurityRefactored
                                    showSecurityCosting={showSecurityCosting}
                                    preloadedData={securityData}
                                    setPreloadedData={setSecurityData}
                                    dataLoaded={securityDataLoaded}
                                    setDataLoaded={setSecurityDataLoaded}
                                    selectedSite={securitySelectedSite}
                                    setSelectedSite={setSecuritySelectedSite}
                                    dateFilters={securityDateFilters}
                                    setDateFilters={setSecurityDateFilters}
                                    selectedFinancialWeek={securityFinancialWeek}
                                    setSelectedFinancialWeek={setSecurityFinancialWeek}
                                    createShiftVals={securityCreateShiftVals}
                                    setCreateShiftVals={setSecurityCreateShiftVals}
                                />
                            </div>
                        </div>
                    </div>
                   </>

                  ) : activeComponent === 'tagging' ? (

                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        <div className="d-flex flex-grow-1">
                            <div className="report-table-container" style={{ width: '100%' }}>
                                <TaggingPatrol
                                    ref={taggingRef}
                                    preloadedData={taggingData}
                                    setPreloadedData={setTaggingData}
                                    dataLoaded={taggingDataLoaded}
                                    setDataLoaded={setTaggingDataLoaded}
                                />
                            </div>
                        </div>                      
                   </div>

                ) : activeComponent === 'occurrences' ? (
                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        <div className="d-flex flex-grow-1">
                            <div className="report-table-container" style={{ width: '100%' }}>
                                <Occurrences
                                    ref={occurrenceRef}
                                    preloadedData={occurrenceData}
                                    setPreloadedData={setOccurrenceData}
                                    dataLoaded={occurrenceDataLoaded}
                                    setDataLoaded={setOccurrenceDataLoaded}
                                    activeSubTab={occurrenceTab}
                                    onSubTabChange={setOccurrenceTab}
                                    hasAdvancedOccurrences={!!featureFlags.copiaOccurrenceAdvanced}
                                    onSummaryEnabledChange={setOccurrenceSummaryEnabled}
                                />
                            </div>
                        </div>
                    </div>

                ) : activeComponent === 'fulfillment' ? (
                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        <div className="d-flex flex-grow-1">
                            <div className="report-table-container" style={{ width: '100%' }}>
                                <FulfillmentSummary
                                    securitySummary={securityData.securitySummary || []}
                                    financialWeeks={securityData.financialWeeks || []}
                                    loading={fulfillmentLoading}
                                />
                            </div>
                        </div>
                    </div>

                ) : activeComponent === 'documents' ? (
                    <div className="content-panel d-flex flex-grow-1 flex-column">
                        <div className="d-flex flex-grow-1">
                            <div className="report-table-container" style={{ width: '100%' }}>
                                <Documents
                                    ref={documentRef}
                                    preloadedData={documentData}
                                    setPreloadedData={setDocumentData}
                                    dataLoaded={documentDataLoaded}
                                    setDataLoaded={setDocumentDataLoaded}
                                />
                            </div>
                        </div>
                    </div>

                ) : activeComponent === 'summaryReport' && (
                    <div className="content-panel d-flex flex-grow-1">
                        <div className="report-table-container summary-report-container">
                            <ReportSummaryView />
                        </div>
                    </div>
                )}
                {showAI === true && (
                    <ChatBot email={userId} guid={userId} />
                )}


                <ReportFooter
                    activeComponent={activeComponent}
                    costingTabView={costingTabView}
                    focusMode={focusMode}
                    logo={opusLogos}
                />
            </div>

            {(isMobileOnly || isTablet) && (
                <>
                    {bottomNavExpanded && (
                        <div className="bottom-nav-expanded" onClick={() => setBottomNavExpanded(false)}>
                            <div className="bottom-nav-expanded__panel" onClick={(e) => e.stopPropagation()}>
                                {showAI && (
                                    <button className="bottom-nav-expanded__item" onClick={() => { onExcelExport(); setBottomNavExpanded(false); }}>
                                        <IoDownloadOutline size={20} />
                                        <span>Export</span>
                                    </button>
                                )}
                                {shouldShowQueries && (
                                    <button className="bottom-nav-expanded__item" onClick={() => { setActiveComponent('queries'); setLoading(true); setBottomNavExpanded(false); }}>
                                        <MdEdit size={20} />
                                        <span>Queries</span>
                                    </button>
                                )}
                                {gigPigEnabled && gigPigEnabled[0] === true && (
                                    <button className="bottom-nav-expanded__item" onClick={() => { handleGigpigPortal(); setBottomNavExpanded(false); }}>
                                        <img src={gigpig} alt="" style={{ width: 20, height: 20 }} />
                                        <span>Book Entertainment</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="bottom-nav">
                        {shouldShowBase && (
                            <button className={`bottom-nav__item${activeComponent === 'costing' ? ' bottom-nav__item--active' : ''}`} onClick={() => setActiveComponent('costing')}>
                                <IoHomeOutline size={22} />
                                <span className="bottom-nav__label">Costings</span>
                            </button>
                        )}
                        {shouldShowRequirements && (
                            <button className={`bottom-nav__item${activeComponent === 'security' ? ' bottom-nav__item--active' : ''}`} onClick={() => { setActiveComponent('security'); logFeatureAccess('ManageSecurity'); }}>
                                <HiOutlineUserGroup size={22} />
                                <span className="bottom-nav__label">Security</span>
                            </button>
                        )}
                        <button
                            className={`bottom-nav__item bottom-nav__item--center${bottomNavExpanded ? ' bottom-nav__item--center-active' : ''}`}
                            onClick={() => setBottomNavExpanded(prev => !prev)}
                        >
                            <BsChevronUp size={20} style={{ transition: 'transform 0.3s ease', transform: bottomNavExpanded ? 'rotate(180deg)' : 'none' }} />
                        </button>
                        {showAI ? (
                            <button className="bottom-nav__item" onClick={() => { }}>
                                <BsLightningChargeFill size={22} />
                                <span className="bottom-nav__label">AI</span>
                            </button>
                        ) : (
                            <button className="bottom-nav__item" onClick={onExcelExport}>
                                <IoDownloadOutline size={22} />
                                <span className="bottom-nav__label">Export</span>
                            </button>
                        )}
                        <button className="bottom-nav__item" onClick={handleLogout}>
                            <IoLogOutOutline size={22} />
                            <span className="bottom-nav__label">Logout</span>
                        </button>
                    </div>
                </>
            )}

        </div>
    );
}
export default ManagerReport;
